import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Pause, Play, Settings, Zap, Loader2, Radio, Trash2, Clock, Sparkles } from 'lucide-react'
import {
  useSeriesDetail,
  useSeriesEpisodes,
  useSeriesFeedback,
  usePauseSeries,
  useResumeSeries,
  useGenerateSeriesEpisodeNow,
  useDeleteSeries,
} from '../api/series'
import { SeriesFeedbackDialog } from '../components/series/series-feedback-dialog'
import { SeriesRssButton } from '../components/series/series-rss-button'
import { SeriesPushToggle } from '../components/series/series-push-toggle'
import { SeriesSettingsPanel } from '../components/series/series-settings-panel'
import { SeriesEpisodeRow } from '../components/series/series-episode-row'
import { SeriesUpgradeBanner } from '../components/series/series-upgrade-banner'
import { UpcomingEpisodesSection } from '../components/series/upcoming-episodes-section'
import { GenerationEta } from '../components/series/generation-eta'
import StickyEpisodePlayer from '../components/podcast/sticky-episode-player'
import ConfirmDialog from '../components/ui/confirm-dialog'
import UpgradeDialog from '../components/billing/upgrade-dialog'
import EpisodeTopUpDialog from '../components/series/episode-topup-dialog'
import { useAuth } from '../hooks/use-auth'
import { track } from '../lib/analytics/posthog'
import { SERIES_EPISODE_ENDED_EVENT, type SeriesEpisodeEndedDetail } from '../lib/series-feedback-events'
import type { Podcast } from '../types/podcast'
import type { SeriesEpisode } from '../types/series'
import { PodcastStatusEnum } from '../types/enums'
import { ApiError } from '../api/config'

const SeriesDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const justCreated = Boolean((location.state as { justCreated?: boolean } | null)?.justCreated)
  const [pushCardDismissed, setPushCardDismissed] = useState(false)
  const { user } = useAuth()
  const contentRef = useRef<HTMLDivElement>(null)

  const { data: series, isLoading, isError, refetch: refetchDetail } = useSeriesDetail(id ?? '')
  const { data: episodesData, isLoading: isEpisodesLoading, refetch: refetchEpisodes } = useSeriesEpisodes(id ?? '')
  const { data: feedbackData } = useSeriesFeedback(id ?? '', 1, 100)
  const pauseSeries = usePauseSeries()
  const resumeSeries = useResumeSeries()
  const generateNow = useGenerateSeriesEpisodeNow()
  const deleteSeries = useDeleteSeries()

  const [showSettings, setShowSettings] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState(false)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  const [topUpPrompt, setTopUpPrompt] = useState<{ priceUsd: number; hasSavedPaymentMethod: boolean } | null>(null)
  const [feedbackEpisode, setFeedbackEpisode] = useState<SeriesEpisode | null>(null)
  const [feedbackDialogSource, setFeedbackDialogSource] = useState<'auto' | 'manual' | null>(null)
  const [dismissedAutoPromptEpisodeIds, setDismissedAutoPromptEpisodeIds] = useState<Set<string>>(new Set())

  const isPaidUser = (user?.plan ?? 'free') !== 'free'

  const feedbackByPodcastId = React.useMemo(() => {
    const entries = feedbackData?.feedbackItems ?? []
    return entries.reduce<Record<string, (typeof entries)[number]>>((accumulator, item) => {
      if (!accumulator[item.podcastId]) {
        accumulator[item.podcastId] = item
      }
      return accumulator
    }, {})
  }, [feedbackData?.feedbackItems])

  useEffect(() => {
    if (id) track('series_pageview', { page: 'detail', series_id: id })
  }, [id])

  useEffect(() => {
    setFeedbackEpisode(null)
    setFeedbackDialogSource(null)
    setDismissedAutoPromptEpisodeIds(new Set())
  }, [id])

  // Determine polling requirements
  const needsCoverImagePoll = series && !series.coverImageUrl
  const hasGeneratingEpisode = episodesData?.episodes.some((e) => e.status === 'generating') ?? false
  const hasCompletedEpisode = episodesData?.episodes.some((e) => e.status === 'completed') ?? false
  const isFirstEpisodeGenerating = series && !hasCompletedEpisode && (hasGeneratingEpisode || series.episodeCount === 0)
  const hasNoEpisodesYet = series && series.episodeCount === 0 && (!episodesData || episodesData.episodes.length === 0)
  const showFirstEpisodeGeneratingState = !!(hasNoEpisodesYet || isFirstEpisodeGenerating)

  // Show upgrade banner for either the free preview limit or a paid plan paused by exhausted credits.
  const isFreeWithUsedEpisode = !isPaidUser && hasCompletedEpisode && user?.freeSeriesEpisodeUsed
  const isPausedForNoCredits =
    isPaidUser &&
    series?.status === 'paused' &&
    typeof user?.creditsRemaining === 'number' &&
    user.creditsRemaining <= 0
  const upgradeBannerReason = isFreeWithUsedEpisode
    ? 'free-limit'
    : isPausedForNoCredits
      ? 'no-credits'
      : null

  // Poll while cover image is missing, an episode is generating, or waiting for first episode to appear
  const needsPoll = needsCoverImagePoll || hasGeneratingEpisode || hasNoEpisodesYet || isFirstEpisodeGenerating
  useEffect(() => {
    if (!needsPoll) return
    const interval = setInterval(() => {
      void refetchEpisodes()
      void refetchDetail()
    }, 3000)
    return () => clearInterval(interval)
  }, [needsPoll, refetchEpisodes, refetchDetail])

  useEffect(() => {
    if (!series?.id) return

    const handleEpisodeEnded = (event: Event) => {
      const detail = (event as CustomEvent<SeriesEpisodeEndedDetail>).detail
      if (!detail?.episodeId || detail.seriesId !== series.id) return
      if (dismissedAutoPromptEpisodeIds.has(detail.episodeId)) return
      if (feedbackByPodcastId[detail.episodeId]) return

      const episode = episodesData?.episodes.find((candidate) => candidate.id === detail.episodeId)
      if (!episode || !episode.audioFileUrl || episode.status !== 'completed') return

      setFeedbackEpisode(episode)
      setFeedbackDialogSource('auto')
    }

    window.addEventListener(SERIES_EPISODE_ENDED_EVENT, handleEpisodeEnded)
    return () => window.removeEventListener(SERIES_EPISODE_ENDED_EVENT, handleEpisodeEnded)
  }, [dismissedAutoPromptEpisodeIds, episodesData?.episodes, feedbackByPodcastId, series?.id])

  const openFeedbackDialog = (episode: SeriesEpisode) => {
    setFeedbackEpisode(episode)
    setFeedbackDialogSource('manual')
  }

  const handleFeedbackDialogOpenChange = (open: boolean) => {
    if (open) return

    if (feedbackDialogSource === 'auto' && feedbackEpisode && !feedbackByPodcastId[feedbackEpisode.id]) {
      setDismissedAutoPromptEpisodeIds((current) => new Set(current).add(feedbackEpisode.id))
    }

    setFeedbackEpisode(null)
    setFeedbackDialogSource(null)
  }

  const handleFeedbackSubmitted = () => {
    if (!feedbackEpisode) return
    setDismissedAutoPromptEpisodeIds((current) => {
      if (!current.has(feedbackEpisode.id)) return current
      const next = new Set(current)
      next.delete(feedbackEpisode.id)
      return next
    })
  }

  const handlePauseResume = () => {
    if (!id) return
    if (series?.status === 'active') {
      void pauseSeries.mutateAsync(id)
    } else {
      // Free users who have used their free episode must upgrade
      if (!isPaidUser && user?.freeSeriesEpisodeUsed) {
        setShowUpgradeDialog(true)
        return
      }
      void resumeSeries.mutateAsync(id)
    }
  }

  const handleGenerateNow = () => {
    if (!id) return
    void generateNow.mutateAsync(id).catch((err) => {
      if (err instanceof ApiError && err.status === 402) {
        const data = (err.data ?? {}) as {
          code?: string
          topupAvailable?: boolean
          topupPriceUsd?: number
          upgradeRequired?: boolean
        }
        if (data.code === 'no_episodes_remaining') {
          setTopUpPrompt({
            priceUsd: typeof data.topupPriceUsd === 'number' ? data.topupPriceUsd : 1.99,
            hasSavedPaymentMethod: !!data.topupAvailable,
          })
          return
        }
        if (data.upgradeRequired) {
          setShowUpgradeDialog(true)
          return
        }
      }
      console.error('Failed to start episode generation', err)
    })
  }

  const handleTopUpSucceeded = () => {
    if (!id) return
    void generateNow.mutateAsync(id).catch((err) => {
      console.error('Episode generation failed after top-up', err)
    })
  }

  const handleDelete = async () => {
    if (!id) return
    await deleteSeries.mutateAsync(id)
    navigate('/series')
  }

  const formatNextEpisode = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'Not scheduled'
    const date = new Date(dateStr)
    const now = new Date()
    const diff = date.getTime() - now.getTime()
    if (diff < 0) return 'Generating soon…'
    if (diff < 3600000) return `${Math.round(diff / 60000)} min`
    if (diff < 86400000) return `${Math.round(diff / 3600000)} hours`
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  }

  // Map series episodes to Podcast type for StickyEpisodePlayer
  const episodesAsPodcasts: Podcast[] = React.useMemo(() => {
    if (!episodesData?.episodes) return []
    return episodesData.episodes
      .filter((e) => e.audioFileUrl && e.status === 'completed')
      .map((e) => ({
        id: e.id,
        title: e.title,
        audioFileUrl: e.audioFileUrl,
        durationSeconds: e.durationSeconds,
        createdAt: e.createdAt,
        // Fill in required Podcast fields with defaults
        userId: '',
        hostAVoiceId: '',
        hostBVoiceId: '',
        speed: 1,
        volume: 1,
        emotion: 'neutral',
        style: 'conversational',
        durationMinutes: 0,
        languageIso: 'en',
        isGenerating: false,
        podcastLines: [],
        status: PodcastStatusEnum.Completed,
        updatedAt: e.createdAt,
      }))
  }, [episodesData?.episodes])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-foreground/30" />
      </div>
    )
  }

  if (isError || !series) {
    return (
      <div className="py-24 text-center">
        <p className="text-sm text-foreground/50">Failed to load series.</p>
        <button onClick={() => navigate('/series')} className="mt-4 cursor-pointer text-sm text-brand-600 hover:underline">
          Back to Series
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-28" ref={contentRef} data-sticky-anchor>
      {/* Back nav */}
      <button
        onClick={() => navigate('/series')}
        className="mb-8 flex cursor-pointer items-center gap-1.5 text-sm font-medium text-foreground/50 transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        All Series
      </button>

      {/* Post-creation prompt: invite the user to enable Web Push notifications.
          Shown only right after a series is created (navigate state) and only on browsers that support push. */}
      {justCreated && !pushCardDismissed && (
        <div className="mb-8">
          <SeriesPushToggle variant="wide" onSubscribed={() => setPushCardDismissed(true)} />
        </div>
      )}

      {/* Header redesign — mobile: compact image+title row, desktop: spacious side-by-side */}
      <div className="mb-6 pb-6 sm:mb-10 sm:pb-10 border-b border-black/5">
        <div className="flex flex-col md:flex-row md:items-start md:gap-8">
          {/* Image + Title row (mobile uses tight horizontal layout, like Spotify/Apple Music) */}
          <div className="flex items-start gap-4 md:block md:flex-none">
            {/* Cover Image — small on mobile, large on desktop */}
            <div className="aspect-square h-20 w-20 shrink-0 sm:h-24 sm:w-24 md:h-48 md:w-48 overflow-hidden rounded-2xl md:rounded-3xl shadow-sm border border-black/5">
              {series.coverImageUrl ? (
                <img
                  src={series.coverImageUrl}
                  alt={`${series.name} cover`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-brand-50 text-brand-300">
                  {needsCoverImagePoll ? (
                    <Loader2 className="h-6 w-6 md:h-8 md:w-8 animate-spin" />
                  ) : (
                    <Radio className="h-8 w-8 md:h-12 md:w-12" />
                  )}
                </div>
              )}
            </div>

            {/* Title block — inline next to image on mobile, hidden here on desktop (rendered below) */}
            <div className="min-w-0 flex-1 md:hidden">
              <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                <StatusBadge status={series.status} isFreePreview={isFreeWithUsedEpisode} />
                {series.status === 'active' && !isFreeWithUsedEpisode && (
                  <NextEpisodeIndicator
                    nextAt={series.nextEpisodeDueAt}
                    formatNextEpisode={formatNextEpisode}
                    onGenerateNow={handleGenerateNow}
                    isGenerating={generateNow.isPending || hasGeneratingEpisode}
                  />
                )}
              </div>
              <h1 className="text-xl font-bold tracking-tight text-foreground leading-tight line-clamp-2">
                {series.name}
              </h1>
            </div>
          </div>

          {/* Content & Actions */}
          <div className="flex flex-col flex-1 min-w-0 justify-between gap-4 mt-4 md:mt-0 md:gap-0">
            {/* Desktop-only title block (mobile title shown above next to image) */}
            <div className="hidden md:block">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <StatusBadge status={series.status} isFreePreview={isFreeWithUsedEpisode} />
                {series.status === 'active' && !isFreeWithUsedEpisode && (
                  <NextEpisodeIndicator
                    nextAt={series.nextEpisodeDueAt}
                    formatNextEpisode={formatNextEpisode}
                    onGenerateNow={handleGenerateNow}
                    isGenerating={generateNow.isPending || hasGeneratingEpisode}
                  />
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-3">
                {series.name}
              </h1>
              {series.description && (
                <p className="text-base text-foreground/60 leading-relaxed max-w-2xl">
                  {series.description}
                </p>
              )}
            </div>

            {/* Mobile description — collapsed under image+title row */}
            {series.description && (
              <p className="md:hidden text-sm text-foreground/60 leading-relaxed line-clamp-3">
                {series.description}
              </p>
            )}

            {/* Primary action: full-width on mobile, inline pill on desktop */}
            <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-2 md:mt-8">
              {isFreeWithUsedEpisode ? (
                <button
                  onClick={() => setShowUpgradeDialog(true)}
                  className="flex w-full md:w-auto cursor-pointer items-center justify-center gap-2 rounded-full bg-brand-600 px-4 py-3 md:py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-700"
                >
                  <Sparkles className="h-4 w-4" /> Subscribe to continue
                </button>
              ) : isPausedForNoCredits ? (
                <button
                  onClick={() => navigate('/billing')}
                  className="flex w-full md:w-auto cursor-pointer items-center justify-center gap-2 rounded-full bg-warning-600 px-4 py-3 md:py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-warning-700"
                >
                  <Zap className="h-4 w-4" /> Top up credits
                </button>
              ) : (
                <button
                  onClick={handlePauseResume}
                  disabled={pauseSeries.isPending || resumeSeries.isPending}
                  className={`flex w-full md:w-auto cursor-pointer items-center justify-center gap-2 rounded-full px-4 py-3 md:py-2 text-sm font-medium shadow-sm transition-all ${
                    series.status === 'active'
                      ? 'bg-white text-foreground border border-black/10 hover:bg-black/5'
                      : 'bg-brand-600 text-white hover:bg-brand-700'
                  } disabled:opacity-50`}
                >
                  {series.status === 'active' ? (
                    <>
                      <Pause className="h-4 w-4 fill-current" /> Pause
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 fill-current" /> Resume
                    </>
                  )}
                </button>
              )}

              {/* Secondary actions: labelled pills, scrollable on mobile, inline on desktop */}
              <div className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:contents md:overflow-visible">
                <SeriesRssButton seriesId={series.id} />

                <SeriesPushToggle />

                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-sm font-medium text-foreground shadow-sm transition-all hover:bg-black/5"
                  title="Settings"
                >
                  <Settings className="h-4 w-4" />
                  <span>Settings</span>
                </button>

                <div className="hidden md:block flex-1" />

                <button
                  onClick={() => setDeleteDialog(true)}
                  className="flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full border border-transparent bg-transparent px-3 py-2 text-sm font-medium text-foreground/40 transition-all hover:bg-danger-50 hover:text-danger-600"
                  title="Delete series"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Settings panel (conditional) */}
      {showSettings && <SeriesSettingsPanel series={series} onClose={() => setShowSettings(false)} />}

      {series.upcomingEpisodes && series.upcomingEpisodes.length > 0 && (
        <UpcomingEpisodesSection episodes={series.upcomingEpisodes} />
      )}

      {/* Episodes */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-foreground">Episodes</h2>

        {isEpisodesLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse rounded-xl border border-black/5 p-4">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-foreground/10" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-2/3 rounded bg-foreground/10" />
                    <div className="h-3 w-1/3 rounded bg-foreground/5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : showFirstEpisodeGeneratingState ? (
          <FirstEpisodeGeneratingState
            startedAt={episodesData?.episodes[0]?.createdAt ?? series.createdAt}
            targetDurationMinutes={series.episodeDurationMinutes}
          />
        ) : !episodesData || episodesData.episodes.length === 0 ? (
          <div className="rounded-xl border border-dashed border-black/10 py-12 text-center">
            <p className="text-sm text-foreground/40">
              {series.status === 'active'
                ? 'Your first episode is being prepared. Check back soon!'
                : 'No episodes yet. Resume the series to start generating episodes.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {episodesData.episodes.map((episode, index) => (
              <SeriesEpisodeRow
                key={episode.id}
                episode={episode}
                seriesId={series.id}
                episodeDurationMinutes={series.episodeDurationMinutes}
                isFreeEpisode={!isPaidUser && index === episodesData.episodes.length - 1 && !!user?.freeSeriesEpisodeUsed}
                hasFeedback={!!feedbackByPodcastId[episode.id]}
                onGiveFeedback={openFeedbackDialog}
              />
            ))}
          </div>
        )}
      </div>

      {/* Upgrade section for free users - positioned after episodes for natural flow */}
      {!showFirstEpisodeGeneratingState && upgradeBannerReason && (
        <SeriesUpgradeBanner reason={upgradeBannerReason} />
      )}

      {/* Series-level sticky player */}
      {episodesAsPodcasts.length > 0 && (
        <StickyEpisodePlayer
          episodes={episodesAsPodcasts}
          seriesId={series.id}
          anchorRef={contentRef}
        />
      )}

      <SeriesFeedbackDialog
        open={!!feedbackEpisode}
        onOpenChange={handleFeedbackDialogOpenChange}
        seriesId={series.id}
        episode={feedbackEpisode}
        existingFeedback={feedbackEpisode ? feedbackByPodcastId[feedbackEpisode.id] : null}
        onSubmitted={handleFeedbackSubmitted}
      />

      {/* Delete dialog */}
      <ConfirmDialog
        open={deleteDialog}
        onOpenChange={setDeleteDialog}
        title="Delete series?"
        description={`Are you sure you want to delete "${series.name}"? Existing episodes will remain in your library, but no new episodes will be generated.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleDelete}
      />

      {/* Upgrade dialog for free users */}
      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        title="Upgrade to continue your series"
        description="You've used your free episode for this series. Subscribe to keep new episodes coming on your schedule."
        bullets={[
          'New episodes delivered automatically on your cadence',
          'All voices and podcast styles',
          'Priority generation',
          'Download episodes as MP3',
        ]}
        paywallId="series_resume"
      />

      {/* Instant top-up dialog (paid users out of episodes) */}
      <EpisodeTopUpDialog
        open={!!topUpPrompt}
        onOpenChange={(open) => {
          if (!open) setTopUpPrompt(null)
        }}
        hasSavedPaymentMethod={topUpPrompt?.hasSavedPaymentMethod ?? false}
        priceUsd={topUpPrompt?.priceUsd ?? 1.99}
        onTopUpSucceeded={handleTopUpSucceeded}
      />
    </div>
  )
}

function StatusBadge({ status, isFreePreview }: { status: string; isFreePreview?: boolean }) {
  if (isFreePreview) {
    return (
      <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">
        Free preview
      </span>
    )
  }

  const styles: Record<string, string> = {
    active: 'bg-success-50 text-success-700',
    paused: 'bg-warning-50 text-warning-700',
  }

  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${styles[status] || 'bg-secondary-100 text-secondary-600'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

function NextEpisodeIndicator({
  nextAt,
  formatNextEpisode,
  onGenerateNow,
  isGenerating,
}: {
  nextAt: string | null | undefined
  formatNextEpisode: (dateStr: string | null | undefined) => string
  onGenerateNow: () => void
  isGenerating: boolean
}) {
  const nextText = formatNextEpisode(nextAt)
  const isGeneratingSoon = nextText === 'Generating soon…'

  if (isGenerating || isGeneratingSoon) {
    return (
      <div className="flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs text-brand-700">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Generating episode…</span>
      </div>
    )
  }

  return (
    <button
      onClick={onGenerateNow}
      className="group flex cursor-pointer items-center gap-2 rounded-full bg-secondary-50 px-3 py-1 text-xs text-foreground/60 transition-colors hover:bg-brand-50 hover:text-brand-700"
    >
      <Clock className="h-3 w-3" />
      <span>Next in {nextText}</span>
      <span className="hidden text-brand-600 group-hover:inline">
        <Zap className="inline h-3 w-3" /> Generate now
      </span>
    </button>
  )
}

function FirstEpisodeGeneratingState({
  startedAt,
  targetDurationMinutes,
}: {
  startedAt: string
  targetDurationMinutes: number
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-brand-100 bg-gradient-to-br from-brand-50 to-white">
      <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-100">
          <Sparkles className="h-8 w-8 text-brand-600" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Creating your free first episode</h3>
        <p className="mt-2 max-w-sm text-sm text-foreground/60">
          We're gathering the latest content based on your interests and turning it into a personalized podcast episode - on the house.
        </p>
        <div className="mt-6 flex items-center gap-2 text-sm text-brand-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          <GenerationEta startedAt={startedAt} targetDurationMinutes={targetDurationMinutes} />
        </div>
      </div>
    </div>
  )
}

export default SeriesDetailPage
